/**
 * @file
 * @deprecated Deprecated since August 8, 2023.
 * This file (reporting.js) is deprecated and will be removed in future versions.
 * Please use the Clickstream package from @zenbusiness/application-commons-clickstream
 * as an alternative for reporting functionalities.
 *
 * For detailed usage instructions, please refer to the README at:
 * (https://github.com/zenbusiness/application-commons/tree/main/packages/clickstream#readme)
 */

import { useCallback } from 'react';

import { UNSAFE_AuthStorage as AuthStorage } from '@zenbusiness/application-commons-authentication';
import { identify as identifyToSentry } from '@zenbusiness/application-commons-logging';
import warn from '@zenbusiness/application-commons-utils/warn';
import { camelCase } from 'lodash';
import { PropTypes } from 'prop-types';

import { UNSAFE_VisitStorage as VisitStorage } from '#client/hooks/useVisit';
import manifest from '#client/manifest';
import {
  ucAll,
  ucFunctional,
  ucMarketing
} from '#client/utils/storageKeys';

import AvoJs from './avo';
import { rudderStackDestination } from './avoRudderstack';
import empty from './empty';

const {
  environment,
  clients: {
    google: {
      enabled: googleEnabled
    },
    sentry: {
      enabled: sentryEnabled
    },
    profitwell: {
      enabled: profitwellEnabled
    }
  }
} = manifest;

const ucAcceptedMarketing = ucMarketing.get()?.toLowerCase() !== 'false';
const ucAcceptedAll = ucAll.get()?.toLowerCase() !== 'false';
const ucAcceptedFunctional = ucFunctional.get()?.toLowerCase() !== 'false';

/* consent configuration */
const hasConsent = ucAcceptedMarketing || ucAcceptedAll || ucAcceptedFunctional;

// redundancy
if (googleEnabled && window.gtag) {
  window.gtag('consent', 'update', {
    ad_storage: hasConsent ? 'granted' : 'denied',
    analytics_storage: hasConsent ? 'granted' : 'denied'
  });
}
/* end consent config */

/**
 * Wrapper function that takes user consent into to consideration before initializing Avo
 */
export const initAvo = () => {
  const acceptedAll = ucAll.get()?.toLowerCase() !== 'false';
  const acceptedFunctional = ucFunctional.get()?.toLowerCase() !== 'false';
  const acceptedMarketing = ucMarketing.get()?.toLowerCase() !== 'false';
  const { obo } = AuthStorage;
  const ZEN_ENV_MAPPING = {
    development: 'dev',
    test: 'dev',
    webpack: 'dev',
    production: 'prod'
  };

  // Rudderstack will need to know if a user consents to any of the three
  if (acceptedAll || acceptedFunctional || acceptedMarketing) {
    // This is the new avo eventing.
    AvoJs.initAvo(
      { env: ZEN_ENV_MAPPING[environment], webDebugger: false, noop: !!obo },
      {},
      {},
      rudderStackDestination
    );
  }
};

const standardErrorFields = ['name', 'message', 'stack'];

// track device type
const zenIsMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const mobileDeviceType = (/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(navigator.userAgent.toLowerCase())) ? 'tablet' : 'mobile';

/**
 * Adjust unique URLs to be non-unique and thus easily aggregatable.
 * E.g. There have been 1337 visits to `/d/company/$uuid/domains`.
 *
 * @param {string} pathname
 * @returns {string} pathname without any customer/business unique identifiers
 */
export const standardizePathname = (pathname) => {
  // React router can leave off the leading slash, add it back for consistency in analytics.
  const pageViewed = pathname?.indexOf('/') !== 0 ? `/${pathname}` : pathname;

  // Remove identifiers from company urls for analytics
  // Replace any UUID found with a non-unique 'uuid'
  return pageViewed.replace(/[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}/g, '$uuid');
};

/**
 * Format string for storage in analytics.
 * This function simply changes value to camel case.
 *
 * @param {string} input
 * @returns {string} camel-cased input
 */
export const formatForAnalytics = (input) => {
  if (!input || input.length < 0) {
    return '';
  }

  return input
  // Replace non-ascii characters to prevent mixpanel grossness
  // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, '')
  // Replace non-alphabetics (E.g. $ /)
    .replace(/\W/g, '-')
  // Replace camel case with hyphen and lowercase
    .replace(/\.?([A-Z]+)/g, (x, y) => `-${y.toLowerCase()}`)
    .replace(/^-/, '')
  // Replace spaces with hyphen
    .replace(/\s+/g, '-')
  // Replace multiple hyphens with a single hyphen
    .replace(/-{2,}/g, '-')
  // All lowercase
    .toLowerCase();
};

/**
 * Generate the identity event properties for analytics
 *
 * @returns {{
 *  userUuid?: string;
 *  accountUuid?: string;
 *  visitUuid?: string;
 * }}
 */
export const generateIdentityEventProps = () => {
  const eventProps = {};
  const {
    accountUuid,
    user
  } = AuthStorage;

  if (user?.userUuid) {
    eventProps.userUuid = user?.userUuid;
  }
  if (accountUuid) {
    eventProps.accountUuid = accountUuid;
  }

  const { visitUUID } = VisitStorage;

  if (visitUUID) {
    eventProps.visitUuid = visitUUID;
  }
  return eventProps;
};

/**
 * Record a scoped exception to sentry
 *
 * @param {object} opts Options
 * @param {Function} opts.callback function to call with sentry eventId
 * @param {Error} opts.error scope attributes generally set from Error
 * @param {object} opts.reactErrorInfo react specific error info setExtras()
 * @param {string} opts.requestId request uuid of attempted API call
 * @param {Error | string} opts.targettedError forwarded to Sentry.captureException()
 * @param {'fatal'|'critical'|'error'|'warning'|'log'|'info'|'debug'|null} opts.level
 */
const scopeSentryException = (opts) => {
  try {
    const {
      callback,
      error,
      reactErrorInfo,
      requestId,
      targettedError,
      // DEFAULTS
      level = null
    } = opts;
    const addlErrAttrs = Object.entries(error).filter(
      ([key]) => (!standardErrorFields.includes(key))
    );

    window.Sentry.withScope((scope) => {
      if (level) {
        scope.setLevel(level);
      }

      if (requestId) {
        scope.setTag('requestId', requestId);
      }

      if (reactErrorInfo) {
        scope.setExtras(reactErrorInfo);
      }

      // This allows error attributes to be visible in Sentry
      addlErrAttrs
        .forEach(([key, value]) => {
          scope.setExtra(key, JSON.stringify(value, null, 2));
        });

      const eventId = window.Sentry.captureException(targettedError);

      if (callback) {
        callback(eventId);
      }
    });
  } catch (err) {
    warn('Attempted call to Sentry failed:', err, opts);
  }
};

/**
 * Record an error level event to sentry
 *
 * @param {Error | object} error error to record in sentry
 * @param {object} info additional info to send to sentry; requestId, errorInfo
 * @param {Function} callback optional function called with sentry eventId
 */
export const recordException = (error, info = {}, callback = null) => {
  const { requestId = null, errorInfo: reactErrorInfo = null } = info;
  // If error isn't an instance of Error (graphql issues)
  const targettedError = error instanceof Error ? error : error.message;

  // debugger;

  if (sentryEnabled && window.Sentry) {
    scopeSentryException({
      callback,
      error,
      reactErrorInfo,
      requestId,
      targettedError
    });
  } else {
    warn('Recorded exception', {
      error,
      reactErrorInfo,
      targettedError,
      requestId
    });
  }
};

/**
 * Record a warning level event to sentry
 *
 * @param {Error | object} error error to record in sentry
 * @param {object} info additional info to send to sentry; requestId, errorInfo
 * @param {Function} callback optional function called with sentry eventId
 */
export const recordWarning = (error, info = {}, callback = null) => {
  const { requestId = null, errorInfo: reactErrorInfo = null } = info;
  // If error isn't an instance of Error (graphql issues)
  const targettedError = error instanceof Error ? error : error.message;

  if (sentryEnabled && window.Sentry) {
    scopeSentryException({
      callback,
      error,
      reactErrorInfo,
      requestId,
      targettedError,
      level: 'warning'
    });
  } else {
    // eslint-disable-next-line no-console
    warn('Recorded warning', {
      error,
      reactErrorInfo,
      targettedError,
      requestId
    });
  }
};

// derive react component properties from dom element
export function getReactPropsFromDomElement(element = {}) {
  // handle events
  const elem = element?.target || element;
  if (!elem) return {};
  try {
    // NOTE: This property is dynamic and meant only for internal use
    const reactPropsKey = Object.keys(elem).find((key) => key.startsWith('__reactProps$'));
    // NOTE: Filter relevant information
    return Object.fromEntries(
      Object.entries(elem[reactPropsKey] || {})
        // NOTE: filter functional items; ie: onClick, onHover
        .filter(([key, value]) => !key.startsWith('on')
          && typeof value !== 'function')
    );
  } catch (err) {
    recordWarning(err);
    return {};
  }
}

/*********************
 * Tracking Functions
 */

/**
 * Link tracking properties
 *
 * @typedef {object} GATrackingProps
 * @property {string} gaAction
 * @property {object} gaOptions
 */

/**
 * This event will fire when a link is pressed
 *
 * @param {GATrackingProps} props
 * @param {HTMLElement} target
 * @augments AvoJs.linkPresseds
 */
export function trackLinkPressed({
  // link pressed props
  linkText,
  // experiment props
  experimentName,
  experimentVariation,
  experimentId,
  // common props
  component = 'web',
  screenName = 'dashboard_homepage',
  pageType = 'dashboard',
  screenDetails,
  destination,
  deviceType,
  // GA Tracking
  gaAction,
  gaOptions
}, target) {
  try {
    // get target from event or target
    const elem = target?.target || target;
    const finalDestination = destination || elem?.href;
    const finalDeviceType = deviceType || zenIsMobile ? mobileDeviceType : 'desktop';
    const identityProps = generateIdentityEventProps();
    AvoJs.linkPressed({
      experimentName,
      experimentVariation,
      component,
      screenDetails,
      experimentId,
      linkText: elem?.innerText || linkText,
      screenName,
      destination: finalDestination,
      deviceType: finalDeviceType,
      pageType,
      ...identityProps
    });
    if (googleEnabled && gaAction && window.gtag) {
      window.gtag('event', gaAction, gaOptions);
    }
  } catch (e) {
    warn('Unable to process button_pressed event: ', e);
  }
}

/**
 * This event will fire when a button is pressed
 *
 * @param {GATrackingProps} props
 * @param {HTMLElement} target
 * @augments AvoJs.buttonPressed
 */
export function trackButtonPressed({
  // button pressed
  buttonName,
  buttonValue,
  buttonText,
  buttonType,
  // experiment props
  experimentName,
  experimentVariation,
  // common props
  component = 'web',
  screenName = 'dashboard_homepage',
  pageType = 'dashboard',
  pageTitle,
  screenDetails,
  destination,
  deviceType,
  // GA Tracking
  gaAction,
  gaOptions
}, target) {
  try {
    const elem = target?.target || target;
    const {
      variant = 'primary',
      color = 'focus',
      size = 'medium'
    } = getReactPropsFromDomElement(target);
    const generatedName = `${variant} ${color} ${size}`;

    const finalDestination = destination || elem?.href;
    const finalDeviceType = deviceType || zenIsMobile ? mobileDeviceType : 'desktop';
    const identityProps = generateIdentityEventProps();

    // When is it a button press
    AvoJs.buttonPressed({
      experimentName,
      experimentVariation,
      component,
      screenDetails,
      // variant-color-size
      buttonType: buttonType || camelCase(generatedName),
      // TODO: map to aria-attr || id || camelCase(innertext.split('\n')[0])
      buttonName,
      buttonValue,
      buttonText: buttonText || elem?.innerText,
      // Derived from spreadsheet/current url
      screenName,
      destination: finalDestination,
      deviceType: finalDeviceType,
      pageType,
      pageTitle,
      ...identityProps
    });

    if (googleEnabled && gaAction && window.gtag) {
      window.gtag('event', gaAction, gaOptions);
    }
  } catch (e) {
    warn('Unable to process button_pressed event: ', e);
  }
}

/**
 * Identify a new user to our reporting libraries
 *
 * @param {object} user
 * @param {string} user.userUuid
 * @param {string} user.email
 */
export const identify = ({ userUuid, email }) => {
  if (empty(email)) {
    warn('Identifying empty username is invalid.');
    return;
  }

  // Sentry does not have any persistence of identity, so we need to reset each time
  // application is reloaded
  identifyToSentry({ userUuid });

  // Setup Profitwell
  if (profitwellEnabled && window.profitwell) {
    window.profitwell('user_email', email?.toLowerCase());
  }
};

/**
 * Unidentify an existing user to our reporting libraries
 */
export const unidentify = () => {
  if (sentryEnabled && window.Sentry) {
    window.Sentry.configureScope((scope) => {
      scope.setUser(null);
    });
  }

  if (profitwellEnabled && window.profitwell) {
    window.profitwell('user_email', '');
  }
};

/**
 * Track information from document search
 *
 * @param {object} trackObject
 * @param {string} trackObject.searchDocumentName -trackObject.searchDocumentName
 * @param {string} trackObject.clickType -trackObject.clickType
 */
export const trackSearch = (trackObject) => {
  const {
    searchDocumentName, clickType
  } = trackObject;

  trackButtonPressed({
    buttonText: 'search',
    buttonType: 'action',
    buttonName: 'search',
    buttonValue: `${clickType - searchDocumentName}`,
    screenName: 'document_center',
    component: 'document_center'
  }, {});
};

/**
 * Track information from document view
 *
 * @param {object} trackDocumentObject
 * @param {string} trackDocumentObject.documentName -trackDocumentObject.documentName
 * @param {string} trackDocumentObject.documentCategory -trackDocumentObject.documentCategory
 */
export const trackDocumentView = (trackDocumentObject) => {
  const {
    documentName, documentCategory, documentUrl
  } = trackDocumentObject;
  trackButtonPressed({
    buttonText: documentName,
    buttonType: 'action',
    buttonName: 'document_view',
    screenName: 'document_viewer',
    component: 'document_center',
    screenDetails: `${documentCategory}-${documentName}`,
    destination: documentUrl
  }, {});
};

/**
 * Track favorited information to
 *
 * @param {object} trackFavoritingObject
 * @param {string} trackFavoritingObject.documentName -trackDocumentObject.documentName
 * @param {string} trackFavoritingObject.documentCategory -trackDocumentObject.documentCategory
 * @param {string} trackFavoritingObject.props.clickType Favorite or unfavorite?
 */
export const trackFavorites = (trackFavoritingObject) => {
  const {
    documentName
  } = trackFavoritingObject;
  trackButtonPressed({
    buttonText: documentName,
    buttonType: 'action',
    buttonName: 'favorite',
    screenName: 'document_library',
    component: 'document_center'
  }, {});
};

/**
 * Track document dropdown menu information
 *
 * NOTE: Update implementation to use hook
 *
 * @param {object} trackDocDropdownObject
 * @param {string} trackDocDropdownObject.pathname -trackDocumentObject.pathname
 * @param {string} trackDocDropdownObject.documentName -trackDocumentObject.documentName
 * @param {string} trackDocDropdownObject.documentCategory -trackDocumentObject.documentCategory
 */
export const trackDocDropdownMenu = (trackDocDropdownObject) => {
  const {
    pathname, documentName, documentCategory
  } = trackDocDropdownObject;

  trackButtonPressed({
    buttonType: 'dropdown_item',
    buttonName: 'document_view',
    buttonValue: `${documentCategory}-${documentName}`,
    screenName: 'document_library',
    component: 'document_center',
    screenDetails: pathname
  }, {});
};

/**
 * Track modal displayed
 *
 * @param {object} props
 * @param {string} props.experimentName
 * @param {string} props.experimentVariation
 * @param {string} props.experimentId
 * @param {string} props.modalName
 */
export const trackModalDisplayed = ({
  experimentName,
  experimentVariation,
  experimentId,
  modalName
}) => {
  const identityProps = generateIdentityEventProps();

  try {
    AvoJs.modalDisplayed({
      experimentName,
      experimentVariation,
      experimentId,
      modalName,
      pageType: 'dashboard',
      deviceType: zenIsMobile ? mobileDeviceType : 'desktop',
      ...identityProps,
      /* There's a problem in avo with this property, this should be deleted when that's fixed */
      customDestinationPageName_: ''
    });
  } catch (e) {
    warn('Unable to process modal_displayed event: ', e);
  }
};

/**************
 * React Hooks
 */

/**
 * React Hook: trackButtonPressed()
 *
 * @param {object} props
 * @param {Function} callback - memoized callback function
 * @returns {Function} memoized eventHandler()
 *
 * @see trackButtonPressed
 */
export function useTrackButtonPressed(props, callback) {
  // const location = useLocation();
  // Memoized Callback handler to return to
  const eventHandler = useCallback((event) => {
    trackButtonPressed(props, event);
    if (typeof callback === 'function') {
      callback(event);
    }
  }, [props, callback]);

  return eventHandler;
}

/**
 * React Hook: trackLinkPressed()
 *
 * @param {object} props
 * @param {Function} callback - memoized callback function
 * @returns {Function} memoized eventHandler()
 *
 * @see trackLinkPressed
 */
export function useTrackLinkPressed(props, callback) {
  // const location = useLocation();
  // Memoized Callback handler to return to
  const eventHandler = useCallback((event) => {
    trackLinkPressed(props, event);
    if (typeof callback === 'function') {
      callback(event);
    }
  }, [props, callback]);

  return eventHandler;
}

/**
 * Data tracking prop type object to validate its shape in a component,
 * Restricted properties will automatically update their allowed values as avo.js file gets updated
 */
export const dataTrackingPropsType = PropTypes.shape({
  buttonName: PropTypes.oneOf(AvoJs?.ButtonName ? Object.values(AvoJs.ButtonName) : ['']),
  buttonText: PropTypes.string,
  buttonType: PropTypes.oneOf(AvoJs?.ButtonType ? Object.values(AvoJs.ButtonType) : ['']),
  buttonValue: PropTypes.string,
  component: PropTypes.oneOf(AvoJs?.Component ? Object.values(AvoJs.Component) : ['']),
  destination: PropTypes.string,
  deviceType: PropTypes.string,
  experimentName: PropTypes.string,
  experimentVariation: PropTypes.string,
  gaAction: PropTypes.string,
  gaOptions: PropTypes.shape({}),
  pageTitle: PropTypes.string,
  pageType: PropTypes.string,
  screenDetails: PropTypes.string,
  screenName: PropTypes.oneOf(AvoJs?.ScreenName ? Object.values(AvoJs.ScreenName) : [''])
});
